import { Box, Typography } from '@mui/material';
import { JsonViewer, defineDataType, nullType } from '@textea/json-viewer';
import { t } from 'i18next';
import { palette } from '../../config/theme/theme';
import { ObjectDataViewerSkeleton } from './skeletons/ObjectDataViewerSkeleton';

const { mainColor, background, valueColor, lineColor, defaultColor, blue, nullColor } = palette.jsonViewer;

const FONT_SIZE = 14;
const LINE_HEIGHT = 1.2;
const MAX_LINES = 18;

const HEIGHT = FONT_SIZE * LINE_HEIGHT * MAX_LINES;

const CUSTOM_THEME = {
  base00: background, // Default Background
  base01: defaultColor, // Lighter Background (Used for status bars, line number and folding marks)
  base02: lineColor, // Selection Background
  base03: defaultColor, // Comments, Invisibles, Line Highlighting
  base04: defaultColor, // Dark Foreground (Used for status bars)
  base05: defaultColor, // Default Foreground, Caret, Delimiters, Operators
  base06: defaultColor, // Light Foreground (Not often used)
  base07: mainColor, // Keys and brackets color
  base08: defaultColor, // Variables, XML Tags, Markup Link Text, Markup Lists, Diff Deleted
  base09: valueColor, // Integers, Boolean, Constants, XML Attributes, Markup Link Url
  base0A: defaultColor, // Classes, Markup Bold, Search Text Background
  base0B: defaultColor, // Strings, Inherited Class, Markup Code, Diff Inserted
  base0C: mainColor, // Array Keys, Support, Regular Expressions, Escape Characters, Markup Quotes
  base0D: defaultColor, // Functions, Methods, Attribute IDs, Headings
  base0E: blue, // Keywords, Storage, Selector, Markup Italic, Diff Changed
  base0F: blue, // Numbers
};

const accessibleNullType = defineDataType<null>({
  ...nullType,
  Component: () => <Box sx={{ backgroundColor: nullColor, color: 'white', display: 'inline', borderRadius: '4px', px: '4px', py: '1px' }}>NULL</Box>,
});

type Props = {
  value: object | null;
  loading: boolean;
  enableClipboard?: boolean;
};

/**
 * A component for rendering and inspecting various data types like objects, arrays, and strings.
 * Leveraging the capabilities of the `JsonViewer`, it displays data structures,
 * accommodating different scenarios such as loading states and data unavailability.
 */
export const ObjectDataViewer = ({ value, loading, enableClipboard }: Props) => {
  if (loading) {
    return <ObjectDataViewerSkeleton />;
  }

  if (!value || Object.keys(value).length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100vh" maxHeight={`${HEIGHT}px`} bgcolor={background}>
        <Typography variant="subtitle1" color="textSecondary">
          {t('noData')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box component="div" tabIndex={0} role="presentation" sx={{ overflow: 'auto', maxHeight: `${HEIGHT}px` }} data-role="object-data-viewer">
      <JsonViewer
        value={value}
        valueTypes={[accessibleNullType]}
        indentWidth={6}
        quotesOnKeys={false}
        displayDataTypes={false}
        enableClipboard={!!enableClipboard}
        rootName=""
        displaySize={false}
        className="json-viewer"
        theme={CUSTOM_THEME}
        style={{
          opacity: '1 !important',
          fontSize: FONT_SIZE,
          lineHeight: LINE_HEIGHT,
          color: mainColor,
          fontFamily: 'Roboto',
          fontStyle: 'normal !important',
          fontWeight: 'normal !important',
          padding: '0.75rem 1rem',
        }}
      />
    </Box>
  );
};
